import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { analyticsAPI } from '../services/api'
import { User, TrendingUp, TrendingDown, ChevronRight, Play, ArrowLeft } from 'lucide-react'

const AnalyticsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTimeframe, setActiveTimeframe] = useState('30d')
  const [loading, setLoading] = useState(true)
  const [analytics, setAnalytics] = useState({
    overview: {
      totalViews: 0,
      totalProjects: 0,
      totalEarnings: 0,
      averageRating: 0,
      responseRate: 0,
      completionRate: 0
    },
    chartData: {
      labels: [],
      earnings: [],
      projects: [],
      views: []
    },
    topPerformers: []
  })
  const [error, setError] = useState(null)


  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await analyticsAPI.getOverview(activeTimeframe)

      if (response.data) {
        setAnalytics(response.data)
      }
    } catch (error) {
      console.error('Error fetching analytics:', error)
      setError('Er is een fout opgetreden bij het laden van analytics data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    fetchAnalytics()
  }, [user, navigate, activeTimeframe])

  // Handle timeframe change
  const handleTimeframeChange = (timeframe) => {
    setActiveTimeframe(timeframe)
  }

  const timeframes = [
    { value: '7d', label: '7 dagen' },
    { value: '30d', label: '30 dagen' },
    { value: '90d', label: '90 dagen' },
    { value: '1y', label: '1 jaar' }
  ]

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount)
  }

  // Create dynamic icon components
  const DynamicIcon = ({ iconName, className }) => {
    const iconMap = {
      'eye': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>,
      'briefcase': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v10a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0H8"/></svg>,
      'euro': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2z"/></svg>,
      'star': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>,
      'percentage': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l6 6-6 6z"/></svg>,
      'activity': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
    }
    const IconComponent = iconMap[iconName]
    return IconComponent ? <IconComponent /> : null
  }

  const StatCard = ({ title, value, change, icon, color = 'sky' }) => (
    <div className="group relative">
      <div className="relative p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200 hover:border-gray-300 transition-all">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className={`w-10 h-10 md:w-12 md:h-12 bg-${color === 'sky' ? 'blue' : color === 'green' ? 'emerald' : color === 'yellow' ? 'yellow' : 'purple'}-50 rounded-xl flex items-center justify-center`}>
            <DynamicIcon iconName={icon} className={`w-6 h-6 text-${color === 'sky' ? '[#1D8BF8]' : color === 'green' ? 'emerald-600' : color === 'yellow' ? 'yellow-600' : '[#7c3aed]'}`} />
          </div>
          {change && (
            <div className={`text-sm font-medium ${change > 0 ? 'text-emerald-600' : 'text-red-500'} flex items-center`}>
              {change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <div className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1 break-words">{value}</div>
        <div className="text-sm text-[#475569]">{title}</div>
      </div>
    </div>
  )

  const ChartCard = ({ title, data, color = 'sky' }) => (
    <div className="group relative">
      <div className="relative p-6 rounded-2xl bg-white shadow-md border border-gray-200">
        <h3 className="text-xl font-bold mb-6 text-[#0F172A]">{title}</h3>

        {/* Simple bar chart representation */}
        <div className="space-y-4">
          {data.labels.map((label, index) => (
            <div key={label} className="flex items-center">
              <div className="w-12 shrink-0 text-xs sm:text-sm text-[#475569]">{label}</div>
              <div className="flex-1 mx-3 sm:mx-4 min-w-0">
                <div className="h-8 bg-[#F8FAFC] rounded-lg overflow-hidden">
                  <div
                    className={`h-full bg-[#3EB489] rounded-lg transition-all duration-1000`}
                    style={{
                      width: `${(data.earnings[index] / Math.max(...data.earnings)) * 100}%`,
                      animationDelay: `${index * 100}ms`
                    }}
                  />
                </div>
              </div>
              <div className="w-20 shrink-0 text-xs sm:text-sm text-[#0F172A] text-right tabular-nums">
                {formatCurrency(data.earnings[index])}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  if (!user) {
    return null
  }

  return (
    <div className="font-['Inter'] min-h-screen bg-white text-[#0F172A] overflow-x-hidden">

      {/* Header */}
      <header className="w-full flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl">
        <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A]">
          UGC<span className="text-[#1D8BF8]">4you</span>
        </Link>

        {/* Mobiel: de nav hieronder is verborgen, dus een expliciete weg terug naar het dashboard */}
        <Link to="/dashboard" className="md:hidden inline-flex items-center gap-2 min-h-[44px] px-3 -mr-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition">
          <ArrowLeft className="w-5 h-5" />
          Dashboard
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/projects" className="hover:text-[#1D8BF8] transition font-medium">Opdrachten</Link>
          <Link to="/creators" className="hover:text-[#1D8BF8] transition font-medium">Creators</Link>
          <Link to="/dashboard" className="hover:text-[#1D8BF8] transition font-medium">Dashboard</Link>
          <Link to="/analytics" className="text-[#1D8BF8] font-medium">Analytics</Link>

          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold">
                {user.role === 'creator' ? user.firstName : user.contactPerson}
              </div>
              <div className="text-xs text-[#475569]">
                {user.role === 'creator' ? 'Creator' : user.companyName}
              </div>
            </div>
            <div className="w-10 h-10 bg-[#3EB489] rounded-full flex items-center justify-center">
              {user.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" />
              )}
            </div>
          </div>
        </nav>
      </header>

      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="min-w-0">
              <h1 className="text-2xl md:text-4xl font-black mb-2">Analytics Dashboard</h1>
              <p className="text-[#475569] text-base md:text-lg">
                Inzicht in je prestaties en verdiensten
              </p>
            </div>

            {/* Timeframe Selector — wrapt zodat "1 jaar" op 360 px bereikbaar blijft (P1) */}
            <div className="flex flex-wrap gap-2 mt-6 lg:mt-0">
              {timeframes.map((timeframe) => (
                <button
                  key={timeframe.value}
                  onClick={() => handleTimeframeChange(timeframe.value)}
                  disabled={loading}
                  className={`px-3 md:px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                    activeTimeframe === timeframe.value
                      ? 'bg-[#3EB489] text-white'
                      : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100 hover:text-[#0F172A]'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {timeframe.label}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D8BF8]"></div>
              <p className="mt-4 text-[#475569]">Analytics worden geladen...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchAnalytics}
                className="mt-4 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl transition"
              >
                Opnieuw proberen
              </button>
            </div>
          )}

          {/* Stats Overview */}
          {!loading && !error && (
            <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
              <StatCard
                title="Totale Weergaven"
                value={analytics.overview.totalViews.toLocaleString()}
                change={12}
                icon="eye"
                color="sky"
              />
              <StatCard
                title={user?.role === 'bedrijf' ? 'Actieve Opdrachten' : 'Voltooide Projecten'}
                value={analytics.overview.totalProjects}
                change={8}
                icon="briefcase"
                color="purple"
              />
              <StatCard
                title={user?.role === 'bedrijf' ? 'Totaal Uitgegeven' : 'Totaal Verdiend'}
                value={formatCurrency(analytics.overview.totalEarnings)}
                change={15}
                icon="euro"
                color="green"
              />
              <StatCard
                title="Gemiddelde Beoordeling"
                value={analytics.overview.averageRating.toFixed(1)}
                change={3}
                icon="star"
                color="yellow"
              />
            </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <ChartCard
              title={user?.role === 'bedrijf' ? 'Maandelijkse Uitgaven' : 'Maandelijkse Inkomsten'}
              data={analytics.chartData}
            />

            <div className="group relative">
              <div className="relative p-6 rounded-2xl bg-white shadow-md border border-gray-200">
                <h3 className="text-xl font-bold mb-6 text-[#0F172A]">Prestatie Metrics</h3>

                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-[#475569]">Response Rate</span>
                      <span className="text-sm text-[#0F172A]">{analytics.overview.responseRate}%</span>
                    </div>
                    <div className="h-3 bg-[#F8FAFC] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-600 to-emerald-600 rounded-full transition-all duration-1000"
                        style={{ width: `${analytics.overview.responseRate}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-[#475569]">Completion Rate</span>
                      <span className="text-sm text-[#0F172A]">{analytics.overview.completionRate}%</span>
                    </div>
                    <div className="h-3 bg-[#F8FAFC] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#3EB489] rounded-full transition-all duration-1000"
                        style={{ width: `${analytics.overview.completionRate}%` }}
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm text-[#475569] mb-2">Trend Analyse</div>
                    <div className="flex items-center text-emerald-600">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      <span className="text-sm">+18% deze maand</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="relative">
            <div className="relative p-6 rounded-2xl bg-white shadow-md border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h3 className="text-xl font-bold text-[#0F172A]">
                  {user?.role === 'bedrijf' ? 'Top Performing Opdrachten' : 'Best Presterende Content'}
                </h3>

                <Link
                  to="/projects"
                  className="text-sm text-[#1D8BF8] hover:text-[#1670c9] transition inline-flex items-center"
                >
                  Bekijk alles
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>

              {/* Mobiel: kaart per item (een geplette 4-kolomstabel laat ~50 px over voor de naam) */}
              <div className="md:hidden divide-y divide-gray-100 border-t border-gray-200">
                {analytics.topPerformers.map((item, index) => (
                  <div key={index} className="py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Play className="w-4 h-4 text-[#1D8BF8]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#0F172A] break-words">{item.name}</div>
                        <div className="text-xs text-[#475569]/50">#{index + 1} trending</div>
                      </div>
                      <div className="text-sm font-medium text-[#0F172A] whitespace-nowrap">{formatCurrency(item.revenue)}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pl-[52px] text-xs text-[#475569]">
                      <span>{item.views.toLocaleString()} weergaven</span>
                      <span className="text-emerald-600">{item.engagement} engagement</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 text-sm font-medium text-[#475569]">Content</th>
                      <th className="text-right py-3 text-sm font-medium text-[#475569]">Weergaven</th>
                      <th className="text-right py-3 text-sm font-medium text-[#475569]">Engagement</th>
                      <th className="text-right py-3 text-sm font-medium text-[#475569]">
                        {user?.role === 'bedrijf' ? 'Besteed' : 'Verdiend'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {analytics.topPerformers.map((item, index) => (
                      <tr key={index} className="hover:bg-[#F8FAFC] transition">
                        <td className="py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                              <Play className="w-4 h-4 text-[#1D8BF8]" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-[#0F172A]">{item.name}</div>
                              <div className="text-xs text-[#475569]/50">#{index + 1} trending</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right py-4 text-sm text-[#0F172A]">
                          {item.views.toLocaleString()}
                        </td>
                        <td className="text-right py-4">
                          <span className="text-sm text-emerald-600">{item.engagement}</span>
                        </td>
                        <td className="text-right py-4 text-sm text-[#0F172A] font-medium">
                          {formatCurrency(item.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AnalyticsPage